/**
 * product-form.js — <product-form> custom element (framework-free)
 *
 * Consolidated from the old Alpine productForm data component. Owns the
 * entire buy-box state:
 *   - variants list (parsed from data-variants JSON)
 *   - current selected options + matched variant
 *   - quantity
 *   - ATC state (loading / success / error) via class toggles on the button
 *
 * Renders display slots marked with [data-ref="..."]:
 *   price          → formatted current variant price × quantity
 *   price-each     → formatted current variant unit price
 *   compare        → formatted compare_at_price (hidden if no sale)
 *   save-amount    → formatted savings (hidden if no sale)
 *   sale-percent   → "-XX%" (hidden if no sale)
 *   availability   → stock label ("En stock" / "Épuisé")
 *   inventory      → integer inventory_quantity
 *
 * Emits CustomEvents on the document (bubbling):
 *   variant:changed { variant, featuredImageId, productForm }
 *   product:added   { variant, cart }
 *   product:error   { message }
 */

import { Component } from '@theme/component';
import { formatMoney } from '@theme/cart-api';
import { cartState } from '@theme/global';

export class ProductForm extends Component {
  #variants = [];
  #optionNames = [];      // ordered list of option.name ("Size", "Color", …)
  #optionCount = 0;       // number of product options (1, 2, or 3)
  #currentVariant = null;
  #options = { option1: null, option2: null, option3: null };
  #quantity = 1;
  #errorEl = null;
  #i18n = {};
  #parseFailed = false;

  onConnect() {
    this.#i18n = {
      soldOut: this.dataset.i18nSoldOut || 'Épuisé',
      inStock: this.dataset.i18nInStock || 'En stock',
      lowStock: this.dataset.i18nLowStock || 'Stock limité',
      selectOption: this.dataset.i18nSelectOption || 'Veuillez sélectionner toutes les options du produit.',
      errorGeneric: this.dataset.i18nErrorGeneric || 'Ajout au panier impossible. Veuillez réessayer.',
      errorBadData: this.dataset.i18nErrorBadData || 'Ce produit est momentanément indisponible. Rechargez la page.'
    };

    this.#loadVariants();

    // If JSON parsing failed, disable the form entirely and show an error.
    // This prevents a broken-state ATC from sending empty variant ids.
    if (this.#parseFailed) {
      this.#disableForAllUsers(this.#i18n.errorBadData);
      return;
    }

    this.#seedFromInitial();
    this.#render();

    const form = this.querySelector('form');
    if (form) this.on(form, 'submit', (e) => this.#handleSubmit(e));

    // Variant picker inputs (native <input type="radio" name="options[XXX]">)
    this.on(this, 'change', (e) => {
      const input = e.target.closest('input[type="radio"][name^="options["]');
      if (input) {
        const posAttr = input.dataset.optionPosition;
        const position = posAttr ? parseInt(posAttr, 10) : this.#inferOptionPositionFromName(input.name);
        if (position) this.setOption('option' + position, input.value);
        return;
      }
      // Quantity — <quantity-selector> owns the +/− buttons and dispatches
      // native `change` on the <input name="quantity">. We listen only for
      // `change` (not `input`) to avoid double renders when Tab-blurring
      // out of the field triggers both events.
      const qtyInput = e.target.closest('input[name="quantity"]');
      if (qtyInput) {
        const n = parseInt(qtyInput.value, 10);
        this.#quantity = Number.isFinite(n) && n > 0 ? n : 1;
        this.#renderPrice();
        this.#renderAtcLabel();
      }
    });
  }

  #loadVariants() {
    const data = this.dataset.variants || this.dataset.product;
    if (!data) {
      this.#parseFailed = true;
      return;
    }
    try {
      const parsed = JSON.parse(data);
      if (!Array.isArray(parsed) || parsed.length === 0) {
        this.#parseFailed = true;
        console.error('[product-form] data-variants is empty or not an array');
        return;
      }
      this.#variants = parsed;

      // Discover how many options the product uses by inspecting the first
      // variant — Shopify puts null on unused option slots.
      const v = this.#variants[0];
      if (v.option3 != null) this.#optionCount = 3;
      else if (v.option2 != null) this.#optionCount = 2;
      else this.#optionCount = 1;

      // Build an ordered list of option names from data-option-position
      // radio inputs so we can reverse-lookup names → positions.
      const nameByPos = new Map();
      this.querySelectorAll('input[type="radio"][name^="options["]').forEach((input) => {
        const pos = parseInt(input.dataset.optionPosition, 10);
        if (!Number.isFinite(pos)) return;
        const match = input.name.match(/options\[(.+)\]/);
        if (match && match[1]) nameByPos.set(pos, match[1]);
      });
      this.#optionNames = [nameByPos.get(1) || '', nameByPos.get(2) || '', nameByPos.get(3) || ''];
    } catch (err) {
      console.error('[product-form] bad data-variants JSON', err);
      this.#parseFailed = true;
    }
  }

  #seedFromInitial() {
    const initialId = parseInt(this.dataset.initialVariantId || '0', 10);
    let variant = this.#variants.find(v => v.id === initialId);
    if (!variant && this.#variants.length > 0) variant = this.#variants[0];
    if (!variant) return;
    this.#currentVariant = variant;
    this.#options = {
      option1: variant.option1 || null,
      option2: variant.option2 || null,
      option3: variant.option3 || null
    };
    // Sync hidden id input if present
    const idInput = this.querySelector('input[name="id"]');
    if (idInput) idInput.value = variant.id;
  }

  /**
   * Reverse-lookup the option position from `options[Name]` input names,
   * using the #optionNames map built during #loadVariants. Returns null if
   * the name is unknown.
   */
  #inferOptionPositionFromName(name) {
    const m = name.match(/options\[(.+)\]/);
    if (!m || !m[1]) return null;
    const idx = this.#optionNames.indexOf(m[1]);
    return idx >= 0 ? idx + 1 : null;
  }

  get currentVariant() { return this.#currentVariant; }
  get quantity() { return this.#quantity; }

  setOption(key, value) {
    this.#options[key] = value;
    const variant = this.#findMatchingVariant();
    if (variant) {
      this.#currentVariant = variant;
      const idInput = this.querySelector('input[name="id"]');
      if (idInput) idInput.value = variant.id;

      // Update URL (replace, don't push)
      try {
        const url = new URL(window.location.href);
        url.searchParams.set('variant', variant.id);
        window.history.replaceState({}, '', url);
      } catch {}

      const featuredImageId = variant.featured_image?.id || null;
      this.#render();
      // Single dispatch: Component.emit() uses `composed: true, bubbles: true`,
      // so the event bubbles all the way up to document and any listener on
      // document (gallery, analytics, etc.) catches it. No need for a second
      // document.dispatchEvent which would fire a duplicate with different
      // detail shape.
      this.emit('variant:changed', { variant, featuredImageId, productForm: this });
    } else {
      this.#currentVariant = null;
      this.#render();
    }
  }

  /**
   * STRICT matching: ALL option slots that this product uses must be
   * populated, and they must all match. The previous permissive fallback
   * (`|| !this.#options.option2`) could silently match a partial selection
   * to ANY variant with the same option1, returning a wrong price.
   */
  #findMatchingVariant() {
    // Require all used options to be selected before attempting a match.
    for (let i = 1; i <= this.#optionCount; i++) {
      if (!this.#options['option' + i]) return null;
    }
    return this.#variants.find((v) => {
      for (let i = 1; i <= this.#optionCount; i++) {
        const key = 'option' + i;
        if (v[key] !== this.#options[key]) return false;
      }
      return true;
    }) || null;
  }

  #render() {
    this.#renderPrice();
    this.#renderAvailability();
    this.#renderSwatchSelection();
    this.#renderSelectedValues();
    this.#renderAtcLabel();
    this.#renderLowStock();
  }

  #renderSelectedValues() {
    // <span data-ref="selected-value-1|2|3"> — short label next to the option title
    for (let pos = 1; pos <= 3; pos++) {
      const el = this.querySelector('[data-ref="selected-value-' + pos + '"]');
      if (el) el.textContent = this.#options['option' + pos] || '';
    }
  }

  #renderAtcLabel() {
    const labelEl = this.querySelector('[data-ref="atc-label"]');
    if (!labelEl) return;
    const btn = labelEl.closest('[type="submit"]');
    if (!btn) return;
    const v = this.#currentVariant;
    const inStock = btn.dataset.labelInStock || 'Ajouter au panier';
    const soldOut = btn.dataset.labelSoldOut || 'Épuisé';
    const showPrice = btn.dataset.showPrice === '1';
    if (v && v.available) {
      const base = showPrice ? inStock + ' — ' + formatMoney(v.price * Math.max(1, this.#quantity)) : inStock;
      labelEl.textContent = base;
    } else {
      labelEl.textContent = soldOut;
    }
  }

  #renderLowStock() {
    const banner = this.querySelector('[data-ref="low-stock"]');
    if (!banner) return;
    const v = this.#currentVariant;
    // Number.isFinite guard: `parseInt("0", 10)` is a valid 0 and must be
    // treated as "threshold disabled", not cascaded to fallback 10.
    const parsed = parseInt(banner.dataset.threshold, 10);
    const threshold = Number.isFinite(parsed) ? parsed : 10;
    if (threshold > 0 && v && v.inventory_quantity > 0 && v.inventory_quantity <= threshold) {
      banner.hidden = false;
      const countEl = banner.querySelector('[data-ref="inventory"]');
      if (countEl) countEl.textContent = String(v.inventory_quantity);
      // Merchant-customizable template: replaces [count] placeholder with
      // the live inventory number. Falls back to legacy textContent update
      // if no template attribute is present (backwards compat).
      const textEl = banner.querySelector('[data-ref="urgency-text"]');
      const tpl = banner.dataset.urgencyTemplate || '';
      if (textEl && tpl) {
        textEl.textContent = tpl.replace('[count]', String(v.inventory_quantity));
      }
    } else {
      banner.hidden = true;
    }
  }

  #renderPrice() {
    const v = this.#currentVariant;
    this.querySelectorAll('[data-ref="price"]').forEach(el => {
      el.textContent = v ? formatMoney(v.price * Math.max(1, this.#quantity)) : '';
      // DS §11 Price: current price turns red on sale. Keep the class in sync
      // with the live variant so switching to/from a sale variant recolors it.
      el.classList.toggle('product-info__price-current--sale', !!(v && v.compare_at_price > v.price));
    });
    this.querySelectorAll('[data-ref="price-each"]').forEach(el => {
      el.textContent = v ? formatMoney(v.price) : '';
    });
    this.querySelectorAll('[data-ref="compare"]').forEach(el => {
      if (v && v.compare_at_price > v.price) {
        el.textContent = formatMoney(v.compare_at_price);
        el.hidden = false;
      } else {
        el.hidden = true;
      }
    });
    this.querySelectorAll('[data-ref="save-amount"]').forEach(el => {
      if (v && v.compare_at_price > v.price) {
        el.textContent = formatMoney(v.compare_at_price - v.price);
        el.hidden = false;
      } else {
        el.hidden = true;
      }
    });
    this.querySelectorAll('[data-ref="sale-percent"]').forEach(el => {
      if (v && v.compare_at_price > v.price) {
        const pct = Math.round(((v.compare_at_price - v.price) / v.compare_at_price) * 100);
        // DS §3: minus sign U+2212 + non-breaking space before % → "−20 %"
        el.textContent = '−' + pct + ' %';
        el.hidden = false;
      } else {
        el.hidden = true;
      }
    });
  }

  #renderAvailability() {
    const v = this.#currentVariant;
    const available = v && v.available;
    this.classList.toggle('is-sold-out', !available);

    this.querySelectorAll('[data-ref="availability"]').forEach(el => {
      el.textContent = available ? this.#i18n.inStock : this.#i18n.soldOut;
      el.classList.toggle('is-available', !!available);
      el.classList.toggle('is-sold-out', !available);
    });
    this.querySelectorAll('[data-ref="inventory"]').forEach(el => {
      el.textContent = v ? String(v.inventory_quantity || 0) : '0';
    });

    // Disable ATC button if sold out
    const atc = this.querySelector('[type="submit"]');
    if (atc) {
      atc.disabled = !available;
      atc.setAttribute('aria-disabled', available ? 'false' : 'true');
    }
  }

  #renderSwatchSelection() {
    this.querySelectorAll('input[type="radio"][name^="options["]').forEach(input => {
      const label = input.closest('label');
      if (!label) return;
      const pos = input.dataset.optionPosition;
      if (!pos) return;
      const position = parseInt(pos, 10);
      const optKey = 'option' + pos;
      const match = this.#options[optKey] === input.value;
      input.checked = match;
      label.classList.toggle('is-active', match);
      // DS §9/§11: a swatch whose value yields no in-stock variant (given the
      // other options currently selected) is struck through + dimmed.
      label.classList.toggle('is-unavailable', !this.#isOptionValueAvailable(position, input.value));
    });
  }

  /**
   * True if picking `value` for option `position` — while keeping every OTHER
   * already-selected option fixed — matches at least one variant that is in
   * stock. Options the user hasn't chosen yet are left unconstrained, so a
   * value is only "unavailable" when EVERY reachable variant for it is sold
   * out or nonexistent. Works for 1-, 2- and 3-option products.
   */
  #isOptionValueAvailable(position, value) {
    return this.#variants.some((variant) => {
      if (variant['option' + position] !== value) return false;
      for (let i = 1; i <= this.#optionCount; i++) {
        if (i === position) continue;
        const selected = this.#options['option' + i];
        if (selected && variant['option' + i] !== selected) return false;
      }
      return variant.available;
    });
  }

  #validate() {
    if (!this.#variants || this.#variants.length === 0) return null;
    const variant = this.#currentVariant;
    if (!variant) return this.#i18n.selectOption;
    if (!variant.available) return this.#i18n.soldOut;
    return null;
  }

  #showError(message) {
    const form = this.querySelector('form');
    if (!form) return;
    if (!this.#errorEl || !this.#errorEl.isConnected) {
      this.#errorEl = document.createElement('div');
      this.#errorEl.className = 'product-form__error';
      this.#errorEl.setAttribute('role', 'alert');
      this.#errorEl.setAttribute('aria-live', 'assertive');
      const actions = form.querySelector('.product-form__actions') || form;
      actions.appendChild(this.#errorEl);
    }
    this.#errorEl.textContent = message;
    this.#errorEl.hidden = false;
  }

  #clearError() {
    if (this.#errorEl) {
      this.#errorEl.textContent = '';
      this.#errorEl.hidden = true;
    }
  }

  /**
   * When data-variants JSON is malformed, the form is structurally broken —
   * submitting would POST an empty id. Disable inputs + submit + show the
   * error banner so the user isn't confused by an unresponsive button.
   */
  #disableForAllUsers(message) {
    const form = this.querySelector('form');
    if (!form) return;
    form.querySelectorAll('input, button, select, textarea').forEach((el) => {
      el.disabled = true;
      el.setAttribute('aria-disabled', 'true');
    });
    this.#showError(message);
  }

  async #handleSubmit(e) {
    e.preventDefault();
    this.#clearError();

    const form = e.target;
    const btn = form.querySelector('[type="submit"]');

    const validationError = this.#validate();
    if (validationError) {
      this.#showError(validationError);
      if (btn) {
        btn.classList.add('is-error');
        setTimeout(() => btn.classList.remove('is-error'), 1000);
      }
      return;
    }

    if (btn) {
      btn.classList.add('is-loading');
      btn.disabled = true;
      btn.setAttribute('aria-busy', 'true');
    }

    try {
      const id = this.#currentVariant?.id;
      const qtyInput = form.querySelector('input[name="quantity"]');
      // Clamp to a positive integer — prevents sending 0 or negative to /cart/add.js
      // (Shopify would reject but the UX is abrupt). Also handles NaN.
      const rawQty = parseInt(qtyInput?.value || '1', 10);
      const qty = Number.isFinite(rawQty) && rawQty > 0 ? rawQty : 1;

      // Single source of truth: delegate to cartState.add which handles
      // withRetry, fetchCart, _applyCart broadcast, and uiCart.open for us.
      // Keeps all three consumers (product-form, quick-add, cart-items) on
      // the same pipeline so a change in retry policy, error handling, or
      // analytics only needs to happen once in global.js.
      await cartState.add(id, qty);

      if (btn) {
        btn.classList.remove('is-loading');
        btn.classList.add('is-success');
        setTimeout(() => {
          btn.classList.remove('is-success');
          btn.disabled = false;
          btn.removeAttribute('aria-busy');
        }, 1500);
      }
      this.emit('product:added', { variant: this.#currentVariant });
    } catch (err) {
      console.error('[product-form] add failed', err);
      const msg = (err && err.message) ? err.message : this.#i18n.errorGeneric;
      this.#showError(msg);
      if (btn) {
        btn.classList.remove('is-loading');
        btn.classList.add('is-error');
        setTimeout(() => {
          btn.classList.remove('is-error');
          btn.disabled = false;
          btn.removeAttribute('aria-busy');
        }, 1000);
      }
      this.emit('product:error', { message: msg });
    }
  }
}

if (!customElements.get('product-form')) {
  customElements.define('product-form', ProductForm);
}
